package kz.dentalcrm.app.screens.positions.edit

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.FilePlus
import compose.icons.feathericons.FileText
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.delete
import dentalcrm.composeapp.generated.resources.edit
import dentalcrm.composeapp.generated.resources.service_naming
import dentalcrm.composeapp.generated.resources.description
import dentalcrm.composeapp.generated.resources.edit_position_title
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.data.network.response_model.positions.Position
import kz.dentalcrm.app.screens.positions.edit.mvi.EditPositionViewEvent
import kz.dentalcrm.app.screens.positions.edit.mvi.EditPositionViewState
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditPositionDialog(
    position: Position,
    onEdit: () -> Unit,
    onDelete: (Int) -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val screenModel = koinViewModel<EditPositionViewModel>()
    val state by screenModel.viewState.collectAsState()

    LaunchedEffect(state.screenState) {
        when (state.screenState) {
            is EditPositionViewState.ScreenState.Edited -> {
                screenModel.handleEvents(EditPositionViewEvent.Reset)
                onEdit()
                onDismiss()
            }
            is EditPositionViewState.ScreenState.Deleted -> {
                screenModel.handleEvents(EditPositionViewEvent.Reset)
                onDelete(position.id)
                onDismiss()
            }
            else -> Unit
        }
    }

    ModalBottomSheet(
        modifier = Modifier.imePadding()
            .statusBarsPadding(),
        onDismissRequest = {
            screenModel.handleEvents(EditPositionViewEvent.Reset)
            onDismiss()
        },
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                isLoading = false,
                modifier = Modifier.background(Color.Transparent),
                content = {
                    EditPositionContent(
                        position = position,
                        state = state,
                        event = screenModel::handleEvents
                    )
                }
            )
        }
    )
}

@Composable
private fun EditPositionContent(
    position: Position,
    state: EditPositionViewState,
    event: (EditPositionViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
            )
            .verticalScroll(rememberScrollState())
            .fillMaxWidth(),
    ) {
        var name by remember { mutableStateOf(position.name ?: "") }
        var description by remember { mutableStateOf(position.description ?: "") }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.edit_position_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            defaultValue = position.name ?: "",
            leadingIcon = FeatherIcons.FilePlus,
            label = stringResource(Res.string.service_naming),
        ) {
            name = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .heightIn(min = 120.dp)
                .fillMaxWidth(),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text
            ),
            singleLine = false,
            defaultValue = position.description ?: "",
            leadingIcon = FeatherIcons.FileText,
            label = stringResource(Res.string.description),
        ) {
            description = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        Row(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.Center
        ) {
            UIButton(
                modifier = Modifier.weight(1f),
                containerColor = DefaultTheme.colors.error,
                text = stringResource(Res.string.delete),
                isLoading = state.screenState is EditPositionViewState.ScreenState.Deleting,
                onClick = {
                    event(
                        EditPositionViewEvent.DeletePosition(
                            position = position
                        )
                    )
                },
            )
            Spacer(modifier = Modifier.width(8.dp))
            UIButton(
                modifier = Modifier.weight(1f),
                enabled = name.isNotEmpty() && description.isNotEmpty(),
                text = stringResource(Res.string.edit),
                isLoading = state.screenState is EditPositionViewState.ScreenState.Editing,
                onClick = {
                    event(
                        EditPositionViewEvent.EditPosition(
                            position = position,
                            name = name,
                            description = description
                        )
                    )
                },
            )
        }

        Spacer(
            modifier = Modifier.fillMaxWidth().height(32.dp)
                .navigationBarsPadding()
        )
    }
}
